/* MONOREE release fixes 2026-10-09. One FIFO for the theme's cart mutations.
 * Never retries a POST. A response-loss/timeout halts queued writes until navigation.
 * Load before cart.js, product-form.js and standard-actions-override.js.
 */
(() => {
  if (window.MonoreeCart) return;
  let tail = Promise.resolve();
  let pending = 0;
  let revision = 0;
  let blocked = null;
  let revalidation = null;
  let away = false;
  const idleWaiters = new Set();
  const locks = new Map();
  const lockObserver = new MutationObserver(() => updateLocks());
  let observingLocks = false;
  const checkoutAreas = 'cart-drawer .cart-drawer__footer, #main-cart-footer, .cart__dynamic-checkout-buttons';
  const words = {
    lt: ['Krepšelio nepavyko patikrinti. Atidarykite krepšelį prieš bandydami dar kartą.', 'Peržiūrėti krepšelį', 'Krepšelis pasikeitė. Patikrinkite prekes ir kiekį.'],
    lv: ['Neizdevās pārbaudīt grozu. Pirms mēģināt vēlreiz, atveriet grozu.', 'Apskatīt grozu', 'Grozs ir mainījies. Pārbaudiet preces un daudzumu.'],
    et: ['Ostukorvi ei õnnestunud kontrollida. Enne uuesti proovimist avage ostukorv.', 'Vaata ostukorvi', 'Ostukorv on muutunud. Kontrollige tooteid ja koguseid.'],
    pl: ['Nie udało się sprawdzić koszyka. Otwórz go przed ponowną próbą.', 'Zobacz koszyk', 'Koszyk uległ zmianie. Sprawdź produkty i ich ilości.'],
    en: ['The cart could not be verified. Open your cart before trying again.', 'View cart', 'The cart has changed. Check its items and quantities.'],
  };
  const text = (index) => (words[(document.documentElement.lang || 'en').split('-')[0]] || words.en)[index];
  const cartURL = () => window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;

  function updateLocks() {
    const busy = pending > 0 || Boolean(blocked);
    document.documentElement.toggleAttribute('data-monoree-cart-busy', busy);
    document.querySelectorAll('cart-drawer, cart-items, cart-drawer-items').forEach((node) => {
      node.setAttribute('aria-busy', String(busy));
    });
    if (typeof CartItems !== 'undefined') CartItems.mutationPending = busy;
    if (busy) {
      // Section replacements can add checkout controls while a write is pending.
      if (!observingLocks) {
        lockObserver.observe(document.documentElement, { childList: true, subtree: true });
        observingLocks = true;
      }
      document.querySelectorAll(checkoutAreas).forEach((node) => {
        if (!locks.has(node)) locks.set(node, node.inert);
        node.inert = true;
      });
    } else {
      for (const [node, wasInert] of locks) if (node.isConnected) node.inert = wasInert;
      locks.clear();
      lockObserver.disconnect();
      observingLocks = false;
    }
    for (const node of locks.keys()) if (!node.isConnected) locks.delete(node);
  }

  function block(error) {
    blocked = error instanceof Error ? error : new Error(text(0));
    blocked.monoreeCartUncertain = true;
    if (typeof CartItems !== 'undefined') CartItems.needsReview = true;
    updateLocks();
  }

  function enqueue(task) {
    pending += 1;
    updateLocks();
    const result = tail.then(async () => {
      if (!blocked && typeof CartItems !== 'undefined' && CartItems.needsReview) block(new Error(text(0)));
      if (blocked) throw blocked;
      revision += 1;
      if (typeof CartItems !== 'undefined') {
        CartItems.cartMutationRevision += 1;
        CartItems.pendingCartDataPromise = null;
      }
      return task({ revision });
    }).finally(() => {
      pending -= 1;
      updateLocks();
      if (!pending) {
        for (const resolve of idleWaiters) resolve();
        idleWaiters.clear();
      }
    });
    // Keep the queue usable after a known rejected operation, not after an uncertain write.
    tail = result.catch(() => undefined);
    return result;
  }

  function whenIdle() {
    return pending ? new Promise((resolve) => idleWaiters.add(resolve)) : Promise.resolve();
  }

  function revalidate() {
    if (revalidation) return revalidation;
    revalidation = enqueue(async () => {
      try {
        return await refresh();
      } catch (error) {
        block(error);
        window.location.assign(cartURL());
        throw error;
      }
    }).finally(() => { revalidation = null; });
    return revalidation;
  }

  function restoreCart() {
    away = false;
    revalidate().catch((error) => console.error('Cart could not be refreshed:', error));
  }

  window.addEventListener('pagehide', () => { away = true; });
  window.addEventListener('pageshow', (event) => {
    const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
    if (event.persisted || navigation?.type === 'back_forward') restoreCart();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') away = true;
    else if (away) restoreCart();
  });
  window.addEventListener('focus', () => { if (away) restoreCart(); });

  function addReviewLink(container) {
    if (!container || container.querySelector('[data-monoree-cart-review]')) return;
    const link = document.createElement('a');
    link.dataset.monoreeCartReview = '';
    link.className = 'link underlined-link';
    link.href = cartURL();
    link.textContent = text(1);
    container.append(document.createTextNode(' '), link);
  }

  // Called from inside a queued mutation, or when the queue is idle. No mutation here.
  async function refresh() {
    const expectedRevision = revision;
    const sections = new Map();
    for (const node of document.querySelectorAll('cart-drawer, cart-items, cart-drawer-items, cart-notification')) {
      let entries;
      try { entries = node.getSectionsToRender?.() || []; } catch { continue; }
      for (const entry of entries) {
        const id = entry.section || entry.id;
        if (!id || sections.has(id)) continue;
        const root = entry.section ? document.getElementById(entry.id) : document;
        const mount = entry.selector ? root?.querySelector(entry.selector) || (entry.section && root) : document.getElementById(entry.id);
        if (mount) sections.set(id, { mount, rootId: entry.id, selector: entry.selector || '.shopify-section' });
      }
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    const read = async (url) => {
      const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error(text(0));
      return response.json();
    };
    try {
      const keys = [...sections.keys()];
      const requests = [];
      for (let index = 0; index < keys.length; index += 5) {
        requests.push(read(`${cartURL()}?sections=${keys.slice(index, index + 5).map(encodeURIComponent).join(',')}`));
      }
      const [cart, ...fragments] = await Promise.all([read(`${cartURL()}.js`), ...requests]);
      if (expectedRevision !== revision || !Array.isArray(cart?.items) || !Number.isInteger(cart.item_count) || !cart.currency) {
        throw new Error(text(0));
      }
      const content = Object.assign({}, ...fragments);
      let cartPage;
      if (keys.some((id) => typeof content[id] !== 'string')) {
        const response = await fetch(cartURL(), { headers: { Accept: 'text/html' }, cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error(text(0));
        cartPage = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (expectedRevision !== revision) throw new Error(text(0));
      }
      const updates = [...sections].map(([id, { mount, rootId, selector }]) => {
        const raw = content[id];
        if (!mount.isConnected) throw new Error(`${text(0)} [detached: ${id}]`);
        let source;
        if (cartPage) {
          const root = cartPage.getElementById(rootId);
          source = root ? root.querySelector(selector) || root : cartPage.querySelector(selector);
        } else if (typeof raw === 'string') {
          source = new DOMParser().parseFromString(raw, 'text/html').querySelector(selector);
        }
        if (!source) throw new Error(`${text(0)} [selector: ${id} ${selector}]`);
        return { mount, html: source.innerHTML };
      });
      // Validate all fragments before replacing any. Only paint outermost mounts.
      const activeId = document.activeElement?.id;
      updates.filter((a) => !updates.some((b) => a !== b && b.mount.contains(a.mount)))
        .forEach(({ mount, html }) => { mount.innerHTML = html; });
      document.querySelectorAll('cart-drawer, cart-items, cart-drawer-items, #main-cart-items, #main-cart-footer')
        .forEach((node) => node.classList.toggle('is-empty', cart.item_count === 0));
      const drawer = document.querySelector('cart-drawer');
      if (drawer && sections.has('cart-drawer')) {
        drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
        if (drawer.classList.contains('active') && typeof trapFocus === 'function') {
          const inner = drawer.querySelector('.drawer__inner');
          if (inner) trapFocus(inner, (activeId && document.getElementById(activeId)) || inner);
        }
      }
      if (!blocked) {
        if (typeof CartItems !== 'undefined') CartItems.needsReview = false;
        document.querySelectorAll('product-form').forEach((form) => { form.cartNeedsReview = false; });
      }
      updateLocks();
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'monoree-cart-reconcile', sectionsRendered: true, cartData: cart });
      }
      return cart;
    } finally {
      clearTimeout(timer);
      controller.abort();
    }
  }

  // Checkout must not use an intermediate cart, including keyboard activation.
  document.addEventListener('click', (event) => {
    if (!pending && !blocked) return;
    const control = event.target.closest?.('[name="checkout"], a[href]');
    if (!control) return;
    const directCheckout = control.matches('a') && /\/checkout(?:[/?#]|$)/.test(control.getAttribute('href') || '');
    if (directCheckout || control.name === 'checkout') {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('submit', (event) => {
    if ((pending || blocked) && (event.submitter?.name === 'checkout' || /\/checkout(?:[/?#]|$)/.test(event.target.getAttribute('action') || ''))) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  window.MonoreeCart = Object.freeze({ enqueue, whenIdle, refresh, revalidate, block, addReviewLink, text,
    get pending() { return pending; }, get revision() { return revision; }, get blocked() { return Boolean(blocked); },
  });
})();
